import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { API } from "../../../services/api";
import { ehpadShape } from "../adminShapes";
import { btnBlackStyle, errorStyle, inputStyle } from "../styles/adminDashboardStyles";

export function EmailsEditor({ ehpad, onSaved }) {
    const [contactEmails, setContactEmails] = useState([...(ehpad.emailsContact ?? [])]);
    const [faxEmails, setFaxEmails] = useState([...(ehpad.emailsFax ?? [])]);
    const [newContact, setNewContact] = useState("");
    const [newFax, setNewFax] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [dragOver, setDragOver] = useState(null);
    const dragSrc = useRef(null);

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const { data } = await API.patch(`/admin/ehpads/${ehpad.id}/emails`, {
                emailsContact: contactEmails,
                emailsFax: faxEmails,
            });
            onSaved(data);
        } catch (err) {
            setError(err.response?.data?.message ?? "Erreur lors de la sauvegarde.");
        } finally {
            setSaving(false);
        }
    };

    const addEmail = (list, setList, value, setValue) => {
        const trimmed = value.trim();
        if (!trimmed || list.includes(trimmed)) return;
        setList(prev => [...prev, trimmed]);
        setValue("");
    };

    const removeEmail = (setList, index) => {
        setList(prev => prev.filter((_, i) => i !== index));
    };

    const onDragStart = (list, index) => { dragSrc.current = { list, index }; };

    const onDrop = (targetList) => {
        setDragOver(null);
        if (!dragSrc.current) return;
        const { list: srcList, index: srcIndex } = dragSrc.current;
        if (srcList === targetList) return;
        const srcSet = srcList === "contact" ? contactEmails : faxEmails;
        const setSrc  = srcList === "contact" ? setContactEmails : setFaxEmails;
        const setDst  = targetList === "contact" ? setContactEmails : setFaxEmails;
        const email = srcSet[srcIndex];
        setSrc(prev => prev.filter((_, i) => i !== srcIndex));
        setDst(prev => [...prev, email]);
        dragSrc.current = null;
    };

    const renderColumn = ({ list, setList, listKey, label, accentColor, newVal, setNewVal }) => (
        <div
            role="group"
            aria-label={label}
            aria-describedby={`admin-ehpad-${ehpad.id}-${listKey}-hint`}
            onDragOver={e => { e.preventDefault(); setDragOver(listKey); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={() => onDrop(listKey)}
            style={{
                flex: 1, minWidth: "260px",
                background: dragOver === listKey ? (listKey === "contact" ? "#eff6ff" : "#faf5ff") : "white",
                border: `2px dashed ${dragOver === listKey ? accentColor : "#e5e7eb"}`,
                borderRadius: "10px", padding: "14px",
                transition: "background 0.15s, border-color 0.15s",
            }}
        >
            <p style={{ margin: "0 0 10px", fontSize: "11px", fontWeight: 700, color: accentColor, textTransform: "uppercase", letterSpacing: "0.6px" }}>
                {label} <span style={{ fontWeight: 400, opacity: 0.7 }}>({list.length})</span>
            </p>
            <span id={`admin-ehpad-${ehpad.id}-${listKey}-hint`} style={{ display: "none" }}>
                Liste d'adresses email déplaçables par glisser-déposer.
            </span>
            <div
                data-testid={`admin-ehpad-${ehpad.id}-${listKey}-email-list`}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    minHeight: "40px",
                    maxHeight: "220px",
                    overflowY: "auto",
                    paddingRight: "4px",
                    marginBottom: "12px",
                }}
            >
                {list.length === 0 && (
                    <span style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic" }}>
                        {dragOver === listKey ? "Déposer ici" : "Aucun email — glisser ou ajouter"}
                    </span>
                )}
                {list.map((email, i) => (
                    <div
                        key={email}
                        role="option"
                        tabIndex={0}
                        aria-label={email}
                        aria-selected={false}
                        draggable
                        onDragStart={() => onDragStart(listKey, i)}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "5px 8px", fontSize: "13px", color: "#374151", cursor: "grab", userSelect: "none" }}
                    >
                        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ color: "#d1d5db", fontSize: "12px" }}>⠿</span>
                            {email}
                        </span>
                        <button onClick={() => removeEmail(setList, i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "16px", lineHeight: 1, padding: "0 2px" }} title="Supprimer">×</button>
                    </div>
                ))}
            </div>
            <div style={{ display: "flex", gap: "6px" }}>
                <input
                    id={`admin-ehpad-${ehpad.id}-${listKey}-email`}
                    type="email"
                    aria-label={`Ajouter ${label.toLowerCase()}`}
                    placeholder="Ajouter un email..."
                    value={newVal}
                    onChange={e => setNewVal(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addEmail(list, setList, newVal, setNewVal); } }}
                    style={{ ...inputStyle, flex: 1 }}
                />
                <button onClick={() => addEmail(list, setList, newVal, setNewVal)} style={{ ...btnBlackStyle, padding: "9px 14px" }}>+</button>
            </div>
        </div>
    );

    return (
        <div style={{ background: "#f8fafc", borderTop: "1px solid #e5e7eb", padding: "16px 20px" }}>
            <p style={{ margin: "0 0 12px", fontSize: "12px", color: "#9ca3af" }}>
                Glissez une adresse d'une colonne à l'autre pour changer son type.
            </p>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
                {renderColumn({ list: contactEmails, setList: setContactEmails, listKey: "contact", label: "Emails contact", accentColor: "#1d4ed8", newVal: newContact, setNewVal: setNewContact })}
                {renderColumn({ list: faxEmails, setList: setFaxEmails, listKey: "fax", label: "Emails fax", accentColor: "#6c4de6", newVal: newFax, setNewVal: setNewFax })}
            </div>
            {error && <p style={errorStyle}>{error}</p>}
            <button onClick={handleSave} disabled={saving} style={btnBlackStyle}>
                {saving ? "Sauvegarde..." : "Enregistrer"}
            </button>
        </div>
    );
}

EmailsEditor.propTypes = {
    ehpad: ehpadShape.isRequired,
    onSaved: PropTypes.func.isRequired,
};
